import { create } from 'zustand';

import type { FormField } from '@/components/shared/form/form-fields';
import http from '@/lib/http';
import { cmsPageEditorFormsPath } from '@/lib/routes';
import type { FormSettings } from '@/types/form';

export interface ResourceForm {
  id: string;
  title: string;
  description?: string;
  fields: FormField[];
  settings?: FormSettings;
  isDefault?: boolean;
  thirdParty?: boolean;
}

interface RawResourceForm extends Omit<ResourceForm, 'description'> {
  description?: string | null;
}

interface FormsApiResponse {
  data: Array<RawResourceForm>;
}

interface ResourcesStore {
  /** Available forms */
  forms: Array<ResourceForm>;

  /** Loading states */
  isLoadingForms: boolean;

  /** Whether resources have been loaded at least once */
  hasLoadedForms: boolean;

  /** Error states */
  formsError: string | null;

  /**
   * Load forms from the API.
   * Only fetches if not already loaded (call with force=true to refetch).
   */
  loadForms: (force?: boolean) => Promise<Array<ResourceForm>>;

  /**
   * Get the first form (used as default for form widget sections).
   */
  getFirstForm: () => ResourceForm | null;

  reset: () => void;
}

export const useResourcesStore = create<ResourcesStore>((set, get) => ({
  forms: [],

  isLoadingForms: false,
  hasLoadedForms: false,
  formsError: null,

  loadForms: async (force = false) => {
    const { hasLoadedForms, isLoadingForms, forms } = get();

    // Return cached data if already loaded and not forcing refresh
    if (hasLoadedForms && !force) {
      return forms;
    }

    // Prevent duplicate requests
    if (isLoadingForms) {
      // Wait for the current request to complete
      return new Promise((resolve) => {
        const unsubscribe = useResourcesStore.subscribe((state) => {
          if (!state.isLoadingForms) {
            unsubscribe();
            resolve(state.forms);
          }
        });
      });
    }

    set({ isLoadingForms: true, formsError: null });

    try {
      const response = await http.get<FormsApiResponse>(cmsPageEditorFormsPath());
      const loadedForms = response.data.data.map((form) => ({
        ...form,
        description: form.description ?? undefined,
      }));

      set({
        forms: loadedForms,
        isLoadingForms: false,
        hasLoadedForms: true,
        formsError: null,
      });

      return loadedForms;
    } catch (error) {
      console.error('Failed to load forms:', error);
      set({
        isLoadingForms: false,
        formsError: 'Failed to load forms',
      });
      return [];
    }
  },

  getFirstForm: () => {
    const { forms } = get();
    return forms.length > 0 ? forms[0] : null;
  },

  reset: () => {
    set({
      forms: [],
      isLoadingForms: false,
      hasLoadedForms: false,
      formsError: null,
    });
  },
}));
